import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Popup, Segment } from "semantic-ui-react";
import Page from "../components/Page";
import Footer from "../../../features/footer/Footer";
import ReactGA from "react-ga4";
import { getUserState } from "../../../js/helpers/getSignedInUserType";
import { signOutUser } from "../../../features/auth/authActions";
import {
  signOutFirebase,
  AUTH_FACEBOOK_PROVIDER,
  AUTH_PASSWORD_PROVIDER,
} from "../../firestore/firebaseService";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import { asyncActionStart } from "../../async/asyncReducer";
import LoadingComponent from "../../layout/LoadingComponent";
import { callAPI, SEND_VERIFICATION_EMAIL_ENDPOINT } from "../../api/callAPI";
import { useEffectOnce } from "react-use";
import logo from "../../../assets/icons/logo-white.svg";
import { HOME_PAGE_ROUTE } from "../../api/routeConstants";

export default function VerifyEmailAccountOverlay() {
  const dispatch = useDispatch();
  const history = useHistory();

  const { loading } = useSelector((state) => state.async);
  const { isCookieConsent, responsive } = useSelector((state) => state.app);
  const { currentUser } = useSelector((state) => state.auth);
  const { currentUserAccount } = useSelector((state) => state.account);

  useEffectOnce(() => {
    window.scrollTo(0, 0);
  });

  async function handleContinueButton() {
    dispatch(asyncActionStart());
    window.location.reload();
  }

  async function handleSignOut() {
    if (isCookieConsent) {
      ReactGA.event({
        category: "Metrics",
        action: "User sign-out",
        label: getUserState(currentUserAccount),
      });
    }
    try {
      history.push("/");
      dispatch(signOutUser());
      await signOutFirebase();
    } catch (error) {
      toast.error(error.message);
    }
  }

  async function handleSendVerificationEmail() {
    if (isCookieConsent) {
      ReactGA.event({
        category: "Metrics",
        action: "Resent email verification request",
        label: getUserState(currentUserAccount),
      });
    }
    try {
      await callAPI({
        endpoint: SEND_VERIFICATION_EMAIL_ENDPOINT,
        opts: {
          body: {
            email: currentUserAccount.email,
            name: currentUserAccount.firstName,
          },
        },
      });
    } catch (e) {
      toast.error("Could not sent email verification");
    }
  }

  if (!currentUser || !currentUserAccount) {
    history.push(HOME_PAGE_ROUTE);
  }

  return (
    <div>
      <Page
        pageClassName="overlay-page"
        backgroundColour={"rgba(231, 244, 253, 0.3)"}
        title={"Verify your email"}
      >
        <div className="sign-in-page-form-container">
          <Segment
            className={
              !responsive.isMobile ? "sign-in-page" : "sign-in-page-mobile"
            }
            compact
            raised
          >
            <div
              className="sign-up-strapline-container"
              style={{ borderRadius: "12px 12px 0 0" }}
            >
              <img
                className="hello-radius-logo"
                src={logo}
                alt="hello radius"
                data-testid="hello-radius-logo-image-testid"
              />
            </div>
            {currentUserAccount.isEmailVerified && (
              <LoadingComponent content={"Almost there..."} />
            )}
            {[AUTH_PASSWORD_PROVIDER, AUTH_FACEBOOK_PROVIDER].includes(
              currentUser.providerId
            ) ? (
              <div style={{ padding: "25px 30px 35px" }}>
                <h2>Verify your email</h2>
                <div className="body-blue-text" style={{ marginBottom: 15 }}>
                  To continue setting up your Hello Radius account, please
                  verify that this is your email. A request has been sent to:{" "}
                  {currentUserAccount.email}
                </div>
                <p className="body-blue-text">
                  Click the button in the email, then return to this page and
                  click "I have verified my email" to sign in.
                </p>
                <Button
                  className="overwrite-button cta-long-button"
                  type={"button"}
                  style={{ margin: "10px 0 20px 0" }}
                  content={"I have verified my email"}
                  onClick={handleContinueButton}
                  disabled={loading}
                  loading={loading}
                />
                <div className="body-blue-text semi-bold">
                  Didn’t receive an email from us?
                </div>
                <div className="body-blue-text" style={{ margin: "10px 0" }}>
                  <span>Check your spam folder or </span>
                  <Popup
                    content={<div className="popup-text">Email sent!</div>}
                    position="top center"
                    wide
                    on="click"
                    trigger={
                      <span
                        className="text-link"
                        onClick={handleSendVerificationEmail}
                      >
                        resend request
                      </span>
                    }
                  />
                </div>
                <div
                  className="body-blue-text"
                  style={{ margin: "10px 0 20px" }}
                >
                  If the problem persists, contact info@helloradius.com
                </div>
                <div
                  className="body-blue-text text-link"
                  onClick={handleSignOut}
                >
                  Verify later
                </div>
              </div>
            ) : (
              <div className={"center-flex"} style={{ marginTop: 30 }}>
                <h2>Verifying your email</h2>
              </div>
            )}
          </Segment>
        </div>
      </Page>
      <Footer />
    </div>
  );
}
